import React, { useCallback, useEffect, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from './ui/Drawer';
import PortLookupField, { type PortValue } from './PortLookupField';
import { getUnLocodeDetail, mapPort } from '../services/ports.service';

/**
 * Map an EDI-supplied port UN/LOCODE to a CMS port — the React port of V1 edi_mapport /
 * edi_savemapport. Unlike the location / size-type mapping editors (which edit a map-table
 * row), a port maps by STAMPING the unlocode onto a chosen cms.ports row, so this drawer is
 * keyed by the unlocode string (not a recno): it shows the EDI code + its UN/LOCODE place
 * name, lets the user pick a CMS port, and PUTs /cms-lookups/map-port. The mapping is global
 * (every EDI order carrying that unlocode is then named), so the host refetches on save.
 *
 * Mountable standalone from the Pending EDI Orders board, the EDI Info modal, and the EDI
 * Order drawer — the same three places the location / size-type mapping appears.
 */
interface Props {
  unlocode: string;
  onClose: () => void;
  onSaved: () => void;
}

const ReadonlyField: React.FC<{ label: string; children?: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col">
    <span className="text-xs font-medium text-muted mb-1">{label}</span>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {children == null || children === '' ? <span className="text-gray-400">—</span> : children}
    </div>
  </div>
);

const PortMapDrawer: React.FC<Props> = ({ unlocode, onClose, onSaved }) => {
  const code = (unlocode || '').trim().toUpperCase();
  const [detail, setDetail] = useState<{ name: string; country: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [port, setPort] = useState<PortValue>({ recno: 0, name: '' });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    getUnLocodeDetail(code)
      .then((d) => { if (alive) setDetail({ name: d.name, country: d.country }); })
      .catch(() => { if (alive) setDetail({ name: '', country: code.slice(0, 2) }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code]);

  const handleSave = useCallback(async () => {
    if (!port.recno) return;
    setSaveError(null);
    setIsSaving(true);
    try {
      await mapPort(port.recno, code);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to map port.');
      setIsSaving(false);
    }
  }, [port.recno, code, onSaved, onClose]);

  // "<name>, <country>" like V1 (blank name → just the country / nothing).
  const placeLine = detail
    ? [detail.name, detail.country].filter(Boolean).join(', ')
    : '';

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving || loading || !port.recno}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> Map Port</>)}
      </button>
    </div>
  );

  return (
    <Drawer isOpen onClose={onClose} title={`Map Port — ${code}`} position="right" width="42rem" footer={footer}>
      <div className="space-y-5">
        {saveError && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
            {saveError}
          </div>
        )}

        <ReadonlyField label="EDI Port (UN/LOCODE)">{code}</ReadonlyField>
        <ReadonlyField label="Location">{loading ? 'Loading…' : placeLine}</ReadonlyField>

        <div>
          <PortLookupField label="CMS Port" value={port} onChange={setPort} required disabled={isSaving} />
          <p className="mt-1.5 text-xs text-muted">
            Stamps <span>{code}</span> onto the selected CMS port. Every pending EDI
            order carrying this UN/LOCODE will then show that port name.
          </p>
        </div>
      </div>
    </Drawer>
  );
};

export default PortMapDrawer;
